import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Search, X } from 'lucide-react';
import { toast } from '../../../../../../ui/toast';
import { DataTable, NameEmailAvatar } from '../../../../../../ui';
import {
  getInviteList,
  removeFromInviteList,
  sendInvites,
} from '../../../../../../utils/api_service/JobOpenings';
import {
  InviteListItem,
  InviteListResponse,
} from '../../../../../../utils/types/JobOpenings';

export interface InviteListModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobOpeningId: string;
}

export const InviteListModal: React.FC<InviteListModalProps> = ({
  isOpen,
  onClose,
  jobOpeningId,
}) => {
  const [inviteList, setInviteList] = useState<InviteListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({
    search: '',
    applied: '',
    invited: '',
  });

  const fetchInviteList = async () => {
    if (!jobOpeningId) {
      setInviteList([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    try {
      const applied =
        filters.applied === 'applied'
          ? true
          : filters.applied === 'not-applied'
            ? false
            : undefined;
      const response = await getInviteList(
        jobOpeningId,
        page,
        pageSize,
        filters.search || undefined,
        applied,
      );
      if (response.ok) {
        const data: InviteListResponse = await response.json();
        setInviteList(data.results);
        setTotal(data.total);
      } else {
        toast.error('Failed to fetch invite list');
      }
    } catch {
      toast.error('Error fetching invite list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && jobOpeningId) {
      fetchInviteList();
    }
  }, [isOpen, jobOpeningId, page, filters]);

  const filteredCandidates = useMemo(() => {
    return inviteList.filter((c) => {
      const fullName = `${c.firstName} ${c.lastName}`.toLowerCase();
      const matchesSearch =
        !filters.search ||
        fullName.includes(filters.search.toLowerCase()) ||
        c.email.toLowerCase().includes(filters.search.toLowerCase());

      const matchesApplied =
        !filters.applied ||
        (filters.applied === 'applied' && c.applied) ||
        (filters.applied === 'not-applied' && !c.applied);

      const matchesInvited =
        !filters.invited ||
        (filters.invited === 'invited' && c.invited) ||
        (filters.invited === 'pending' && !c.invited);

      return matchesSearch && matchesApplied && matchesInvited;
    });
  }, [inviteList, filters]);

  const handleSelectRow = (rowIndex: number, row: InviteListItem) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(row.id)) {
      newSelected.delete(row.id);
    } else {
      newSelected.add(row.id);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedIds.size === filteredCandidates.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredCandidates.map((c) => c.id)));
    }
  };

  const handleSendInvite = async () => {
    if (selectedIds.size === 0) return;

    try {
      const response = await sendInvites(jobOpeningId, {
        inviteIds: Array.from(selectedIds),
      });
      if (response.ok) {
        toast.success(`Invites sent to ${selectedIds.size} candidates`);
        setSelectedIds(new Set());
        fetchInviteList();
      } else {
        toast.error('Failed to send invites');
      }
    } catch {
      toast.error('Error sending invites');
    }
  };

  const handleSendAllInvites = async () => {
    try {
      const response = await sendInvites(jobOpeningId, { sendAll: true });
      if (response.ok) {
        toast.success('Invites sent to all pending candidates');
        fetchInviteList();
      } else {
        toast.error('Failed to send invites');
      }
    } catch {
      toast.error('Error sending invites');
    }
  };

  const handleRemove = async () => {
    if (selectedIds.size === 0) return;

    const candidatesToRemove = filteredCandidates.filter(
      (c) => selectedIds.has(c.id) && !c.invited,
    );

    if (candidatesToRemove.length === 0) {
      toast.warning('Cannot remove candidates who have already been invited');
      return;
    }

    try {
      const removePromises = candidatesToRemove.map((c) =>
        removeFromInviteList(jobOpeningId, c.id),
      );
      await Promise.all(removePromises);
      toast.success(
        `Removed ${candidatesToRemove.length} candidates from list`,
      );
      setSelectedIds(new Set());
      fetchInviteList();
    } catch {
      toast.error('Error removing candidates from list');
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Candidate Name',
      align: 'left' as const,
      render: (row: InviteListItem) => (
        <NameEmailAvatar
          name={`${row.firstName} ${row.lastName}`}
          email={row.email}
          avatar={''}
        />
      ),
    },
    {
      key: 'invitedAt',
      label: 'Invited On',
      render: (row: InviteListItem) => (
        <Typography variant='body2' sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
          {row.invited && row.invitedAt
            ? new Date(row.invitedAt * 1000).toLocaleDateString()
            : '—'}
        </Typography>
      ),
      align: 'left' as const,
    },
    {
      key: 'applied',
      label: 'Applied',
      render: (row: InviteListItem) => (
        <Chip
          label={row.applied ? 'Yes' : 'No'}
          size='small'
          color={row.applied ? 'success' : 'default'}
          variant='outlined'
          sx={{ fontWeight: 700, fontSize: '0.65rem', height: 20 }}
        />
      ),
      align: 'left' as const,
    },
    {
      key: 'status',
      label: 'Status',
      render: (row: InviteListItem) => (
        <Chip
          label={row.invited ? 'Invited' : 'Pending'}
          size='small'
          color={row.invited ? 'success' : 'default'}
          variant='filled'
          sx={{ fontWeight: 700, fontSize: '0.65rem', height: 20 }}
        />
      ),
      align: 'left' as const,
    },
  ];

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant='h6' sx={{ fontWeight: 700, color: 'text.primary' }}>
          Candibank Invite List
        </Typography>
        <IconButton size='small' onClick={onClose} sx={{ color: 'text.secondary' }}>
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {/* Filters */}
        <Paper
          variant='outlined'
          sx={{ p: 2, borderRadius: 2.5, bgcolor: 'action.hover' }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant='caption' sx={{ display: 'block', mb: 0.5, fontWeight: 700, color: 'text.secondary' }}>
                Search
              </Typography>
              <TextField
                fullWidth
                size='small'
                placeholder='Search by name or email...'
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Search size={16} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 2, bgcolor: 'background.paper', fontSize: '0.8rem' },
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant='caption' sx={{ display: 'block', mb: 0.5, fontWeight: 700, color: 'text.secondary' }}>
                Application Status
              </Typography>
              <FormControl fullWidth size='small'>
                <Select
                  value={filters.applied}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, applied: e.target.value }))
                  }
                  sx={{ borderRadius: 2, bgcolor: 'background.paper', fontSize: '0.8rem' }}
                >
                  <MenuItem value=''>All</MenuItem>
                  <MenuItem value='applied'>Applied</MenuItem>
                  <MenuItem value='not-applied'>Not Applied</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant='caption' sx={{ display: 'block', mb: 0.5, fontWeight: 700, color: 'text.secondary' }}>
                Invite Status
              </Typography>
              <FormControl fullWidth size='small'>
                <Select
                  value={filters.invited || ''}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, invited: e.target.value }))
                  }
                  sx={{ borderRadius: 2, bgcolor: 'background.paper', fontSize: '0.8rem' }}
                >
                  <MenuItem value=''>All</MenuItem>
                  <MenuItem value='invited'>Invited</MenuItem>
                  <MenuItem value='pending'>Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Action Toolbar */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
          <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.secondary' }}>
            {selectedIds.size} candidates selected • {total} total
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant='outlined'
              color='error'
              size='small'
              onClick={handleRemove}
              disabled={selectedIds.size === 0 || loading}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Remove from List
            </Button>
            <Button
              variant='contained'
              color='primary'
              size='small'
              onClick={handleSendInvite}
              disabled={selectedIds.size === 0 || loading}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Send Selected Invites
            </Button>
            <Button
              variant='contained'
              color='success'
              size='small'
              onClick={handleSendAllInvites}
              disabled={loading}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Send All Pending Invites
            </Button>
          </Box>
        </Box>

        {/* Data Table */}
        <Box sx={{ minHeight: 400, flex: 1 }}>
          <DataTable
            data={filteredCandidates}
            columns={columns}
            isLoading={loading}
            hasCheckBox
            onSelectAll={handleSelectAll}
            onSelectRow={(index: number, row: InviteListItem) =>
              handleSelectRow(index, row)
            }
            isRowSelected={(index: number, row: InviteListItem) =>
              selectedIds.has(row.id)
            }
            isAllSelected={
              selectedIds.size > 0 &&
              selectedIds.size === filteredCandidates.length
            }
            isIndeterminate={
              selectedIds.size > 0 &&
              selectedIds.size < filteredCandidates.length
            }
            rowsPerPage={pageSize}
            currentPage={page}
            totalPages={Math.ceil(total / pageSize)}
            totalRecords={total}
            onPageChange={(newPage: number) => setPage(newPage)}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} variant='outlined' sx={{ textTransform: 'none', borderRadius: 2 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InviteListModal;
